<template>
  <div class="flex min-h-0 flex-1 flex-col gap-6">
    <!-- Toolbar -->
    <div class="flex items-center gap-2">
      <div class="relative min-w-0 max-w-sm flex-1">
        <Search
          class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          v-model="localQuery"
          :placeholder="$t('Поиск...')"
          class="h-9 pl-9 text-sm"
          @input="onSearch"
        />
      </div>
      <Button
        variant="outline"
        size="icon"
        class="shrink-0"
        :aria-pressed="showFilters"
        :class="showFilters ? 'bg-muted' : ''"
        @click="showFilters = !showFilters"
      >
        <svg-icon name="huge-filter" class="h-5 w-5 text-muted-foreground" />
      </Button>
      <div class="flex-1" />
      <BaseTabs
        v-if="viewTabs"
        inline
        :tabs="viewTabs"
        :active-tab="activeView"
        @change:tab="$emit('update:activeView', $event)"
      />
    </div>

    <!-- Filters panel -->
    <div v-if="showFilters" class="flex flex-wrap items-center gap-3">
      <div class="home-tabs">
        <button
          v-for="opt in resultOptionsList"
          :key="opt.value"
          type="button"
          class="home-tab"
          :class="{ 'home-tab--active': statusFilter === opt.value }"
          @click="$emit('update:statusFilter', opt.value)"
        >
          {{ opt.label }}
          <span
            v-if="typeof opt.count === 'number'"
            class="ml-1.5 text-xs text-muted-foreground"
          >
            {{ opt.count }}
          </span>
        </button>
      </div>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :placeholder="$t('Выберите период')"
        @update:start-date="onStartDate"
        @update:end-date="onEndDate"
        class="w-[330px]"
        trigger-class="h-9 text-sm"
      />
      <!--      <button-->
      <!--        v-if="startDate || endDate || statusFilter !== 'all'"-->
      <!--        type="button"-->
      <!--        class="text-xs text-muted-foreground hover:text-foreground"-->
      <!--        @click="resetFilters"-->
      <!--      >-->
      <!--        {{ $t("Сбросить") }}-->
      <!--      </button>-->
    </div>

    <!-- Table -->
    <div class="table-area flex min-h-0 flex-1 flex-col">
      <div class="table-scroll min-h-0 flex-1 overflow-auto">
        <Table :loading="loading" :scroll-reset-key="page">
          <TableHeader>
            <TableRow>
              <TableHead class="min-w-[60px] whitespace-nowrap">
                {{ $t("ID") }}
              </TableHead>
              <TableHead class="min-w-[140px] whitespace-nowrap">
                {{ $t("Время") }}
              </TableHead>
              <TableHead class="min-w-[290px] whitespace-nowrap">
                {{ $t("Пользователь") }}
              </TableHead>
              <TableHead class="min-w-[29/0px] whitespace-nowrap">
                {{ $t("Действие") }}
              </TableHead>
              <TableHead class="min-w-[140px] whitespace-nowrap">
                {{ $t("Точность") }}
              </TableHead>
              <TableHead class="min-w-[140px] whitespace-nowrap">
                {{ $t("Результат") }}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableEmpty v-if="!loading && !filteredEntries.length" :colspan="6">
              {{ $t("Нет записей") }}
            </TableEmpty>
            <TableRow
              v-for="item in filteredEntries"
              :key="item.id"
              :class="userById(item.person_id) ? 'cursor-pointer' : ''"
              class="hover:bg-muted/50"
              @click="handleClick(item)"
            >
              <TableCell class="text-muted-foreground">
                {{ item.id }}
              </TableCell>
              <TableCell class="text-muted-foreground">
                {{ formatShortTime(item.timestamp) }}
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2.5">
                  <UserAvatar
                    :identifier="
                      userByIdMap[String(item.person_id || '')]?.identifier ||
                      ''
                    "
                    :seed="item.person_id || String(item.id)"
                    :name="item.person_name"
                    :size="28"
                  />
                  <div class="text-sm font-medium text-foreground">
                    {{ item.person_name || item.person_id || "—" }}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div
                  v-if="item.actor_name || item.actor_id"
                  class="text-sm leading-5"
                >
                  <span class="font-medium text-foreground">
                    {{ item.actor_name || item.actor_id }}
                  </span>
                  <span class="text-muted-foreground pl-1">
                    {{ actionVerb(item.action) }}
                  </span>
                </div>
                <Tag
                  v-else
                  :variant="actionVariant(item.action)"
                  size="sm"
                  class="rounded-md"
                >
                  {{ actionLabel(item.action) }}
                </Tag>
              </TableCell>
              <TableCell>
                <ScoreBar
                  v-if="typeof item.similarity_score === 'number'"
                  :score="item.similarity_score"
                  :result="item.status === 'success' ? 'success' : 'fail'"
                />
                <span v-else class="text-muted-foreground">—</span>
              </TableCell>
              <TableCell>
                <Tag
                  :variant="item.status === 'success' ? 'success' : 'danger'"
                  size="sm"
                  :title="item.reason || ''"
                  class="rounded-md"
                >
                  <span
                    class="inline-block h-1.5 w-1.5 rounded-full"
                    :class="
                      item.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                    "
                  />
                  {{ item.status === "success" ? $t("Успешно") : $t("Ошибка") }}
                </Tag>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <TableFooterWrapper
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        :page-sizes="[10, 20, 50]"
        @update:page-size="$emit('update:pageSize', $event)"
        @update:current-page="$emit('update:page', $event)"
      >
        <template #pagination>
          <Pagination
            v-if="total > pageSize"
            :total="total"
            :items-per-page="pageSize"
            :page="page"
            :sibling-count="1"
            show-edges
            @update:page="$emit('update:page', $event)"
          >
            <PaginationContent v-slot="{ items }">
              <PaginationPrevious />
              <template v-for="(item, index) in items" :key="index">
                <PaginationItem
                  v-if="item.type === 'page'"
                  :value="item.value"
                  :is-active="item.value === page"
                >
                  {{ item.value }}
                </PaginationItem>
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </Pagination>
        </template>
      </TableFooterWrapper>
    </div>
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import { Tag } from "@/shared/ui/tag";
import Button from "@/shared/ui/button/Button.vue";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import { DateRangePicker } from "@/shared/ui/date-picker";
import UserAvatar from "./UserAvatar.vue";
import ScoreBar from "./ScoreBar.vue";
import { formatShortTime } from "./utils";

export default {
  name: "FaceIdAttemptsTable",
  components: {
    Search,
    Input,
    Tag,
    Button,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
    TableFooterWrapper,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    BaseTabs,
    DateRangePicker,
    UserAvatar,
    ScoreBar,
  },
  props: {
    entries: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    userByIdMap: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    statusFilter: { type: String, default: "all" },
    query: { type: String, default: "" },
    counts: {
      type: Object,
      default: () => ({ all: 0, success: 0, failure: 0 }),
    },
    viewTabs: { type: Object, default: null },
    activeView: { type: String, default: "attempts" },
    startDate: { type: String, default: "" },
    endDate: { type: String, default: "" },
  },
  emits: [
    "open-user",
    "update:page",
    "update:pageSize",
    "update:statusFilter",
    "update:query",
    "update:activeView",
    "update:start-date",
    "update:end-date",
  ],
  data() {
    return {
      localQueryValue: this.query,
      searchTimer: null,
      showFilters: false,
    };
  },
  computed: {
    localQuery: {
      get() {
        return this.localQueryValue;
      },
      set(v) {
        this.localQueryValue = v;
      },
    },
    resultOptionsList() {
      return [
        { value: "all", label: this.$t("Все"), count: this.counts.all },
        {
          value: "success",
          label: this.$t("Успешно"),
          count: this.counts.success,
        },
        {
          value: "failure",
          label: this.$t("Ошибки"),
          count: this.counts.failure,
        },
      ];
    },
    filteredEntries() {
      const s = (this.query || "").trim().toLowerCase();
      if (!s) return this.entries;
      return this.entries.filter((a) =>
        [a.person_name, a.person_id, a.action, a.reason, a.id]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    },
  },
  watch: {
    query(value) {
      this.localQueryValue = value;
    },
  },
  methods: {
    formatShortTime,
    userById(id) {
      return this.userByIdMap[String(id)] || null;
    },
    handleClick(item) {
      const user = this.userById(item.person_id);
      if (user) this.$emit("open-user", user);
    },
    actionLabel(action) {
      if (action === "identify") return this.$t("Вход");
      if (action === "enroll") return this.$t("Регистрация");
      if (action === "add_face") return this.$t("Добавление фото");
      if (action === "delete_face") return this.$t("Удаление фото");
      return action || "—";
    },
    actionVerb(action) {
      if (action === "identify") return this.$t("выполнил(а) вход");
      if (action === "enroll") return this.$t("зарегистрировал(а) Face ID");
      if (action === "add_face") return this.$t("добавил(а) фото");
      if (action === "delete_face") return this.$t("удалил(а) фото");
      return action || "";
    },
    actionVariant(action) {
      if (action === "enroll") return "primary";
      if (action === "add_face") return "default";
      if (action === "delete_face") return "danger";
      return "outline";
    },
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$emit("update:query", this.localQueryValue);
      }, 300);
    },
    onStartDate(val) {
      this.$emit("update:start-date", val);
    },
    onEndDate(val) {
      this.$emit("update:end-date", val);
    },
    // resetFilters() {
    //   this.$emit("update:statusFilter", "all");
    //   this.$emit("update:startDate", "");
    //   this.$emit("update:endDate", "");
    // },
  },
};
</script>

<style lang="scss" scoped>
.home-tabs {
  @apply rounded-md;
  display: flex;
  align-items: stretch;
  justify-content: center;
  border: 1px solid hsl(var(--border));
  overflow: hidden;
  width: fit-content;
  height: 36px;

  .home-tab {
    padding: 0 16px;
    font-size: 14px;
    font-weight: 400;
    color: hsl(var(--foreground));
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;

    &:not(:last-child) {
      border-right: 1px solid hsl(var(--border));
    }

    &:hover {
      background: hsl(var(--accent));
    }

    &--active {
      background: hsl(var(--muted));
      font-weight: 500;
    }
  }
}
</style>
